/**
 * Primes configurable components (Phase 6):
 *
 * - `extranet.premiumsListCard` — « Mes primes » list (`PremiumsListCard`).
 *   Layout surface (`_layoutOverrides`, not in the schema): `columns`
 *   `assure` / `produit` / `numeroContrat` / `reference` / `periodeDu` /
 *   `periodeAu` / `ttc` / `situation` / `actions`; `filters.situation`;
 *   `search`; `actions.documents` (the per-row « Documents » button —
 *   `setActionLabel` / `hideAction`); `title`.
 * - `extranet.premiumDocumentsPanel` — « Documents — {NUM_QUI} » side panel
 *   (`PremiumDocumentsPanel`). Layout surface: `items` `nom` /
 *   `categorieDate`; `title` (may keep the `{numQui}` token).
 *
 * Both expose the page's rule context (`premiumsContextSchema`: the base
 * extranet variables + `premiums_count`).
 */

import { withComponentRules } from '@components/shared/configurable';

import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { PREMIUM_DOCUMENTS_PANEL_ID } from './premiumDocumentItems';
import { PremiumDocumentsPanel } from './PremiumDocumentsPanel';
import { premiumsContextSchema } from './premiumsContext';
import { PREMIUMS_LIST_CARD_ID } from './premiumsListConfig';
import { PremiumsListCard } from './PremiumsListCard';
import {
  PREMIUM_DOCUMENTS_PANEL_PREVIEW_PROPS,
  PREMIUM_DOCUMENTS_PANEL_PROP_LABELS,
  PREMIUMS_LIST_PREVIEW_PROPS,
  PREMIUMS_LIST_PROP_LABELS,
  premiumDocumentsPanelDataSchema,
  premiumsListCardDataSchema,
  type PremiumDocumentsPanelDataProps,
  type PremiumsListCardDataProps,
} from './schemas';

/** Ids registered by this domain. */
export const PREMIUM_COMPONENT_IDS = [PREMIUMS_LIST_CARD_ID, PREMIUM_DOCUMENTS_PANEL_ID] as const;

/** Register the Primes components. Idempotent. */
export function registerPremiumComponents(): void {
  registerOnce<PremiumsListCardDataProps>({
    id: PREMIUMS_LIST_CARD_ID,
    displayName: 'Liste des primes',
    module: EXTRANET_MODULE,
    component: PremiumsListCard,
    propsSchema: premiumsListCardDataSchema,
    defaultPropLabels: PREMIUMS_LIST_PROP_LABELS,
    previewProps: PREMIUMS_LIST_PREVIEW_PROPS,
    contextVariables: premiumsContextSchema,
  });

  registerOnce<PremiumDocumentsPanelDataProps>({
    id: PREMIUM_DOCUMENTS_PANEL_ID,
    displayName: 'Documents de la prime',
    module: EXTRANET_MODULE,
    component: PremiumDocumentsPanel,
    propsSchema: premiumDocumentsPanelDataSchema,
    defaultPropLabels: PREMIUM_DOCUMENTS_PANEL_PROP_LABELS,
    previewProps: PREMIUM_DOCUMENTS_PANEL_PREVIEW_PROPS,
    contextVariables: premiumsContextSchema,
  });
}

/** Rule-aware « Mes primes » list — import aliased as `PremiumsListCard`. */
export const PremiumsListCardConfigurable = withComponentRules(
  PremiumsListCard,
  PREMIUMS_LIST_CARD_ID,
);

/** Rule-aware documents panel — import aliased as `PremiumDocumentsPanel`. */
export const PremiumDocumentsPanelConfigurable = withComponentRules(
  PremiumDocumentsPanel,
  PREMIUM_DOCUMENTS_PANEL_ID,
);
